import type { HomeEconomy, HomeWallet } from "@/home/economy";

export type HealthPotionFulfillmentKind =
  | "health_potion"
  | "health_potion_small"
  | "health_potion_medium"
  | "health_potion_large";

export const HEALTH_POTION_TIERS = ["small", "medium", "large"] as const;
export type HealthPotionTier = (typeof HEALTH_POTION_TIERS)[number];

export const HEALTH_POTION_HEAL_BY_TIER: Record<HealthPotionTier, number> = {
  small: 100,
  medium: 500,
  large: 1000,
};

export const HEALTH_POTION_LABEL_TH: Record<HealthPotionTier, string> = {
  small: "ยาเล็ก",
  medium: "ยากลาง",
  large: "ยาใหญ่",
};

export const HEALTH_POTION_SHORT_LABEL: Record<HealthPotionTier, string> = {
  small: "S",
  medium: "M",
  large: "L",
};

export const HEALTH_POTION_DEFAULT_COIN_PRICE: Record<HealthPotionTier, number> = {
  small: 120,
  medium: 550,
  large: 1000,
};

export const HEALTH_POTION_SKU_BY_TIER: Record<HealthPotionTier, string> = {
  small: "home.health_potion.small.v1",
  medium: "home.health_potion.medium.v1",
  large: "home.health_potion.large.v1",
};

export type HealthPotionStashItemId =
  | "health_potion_small"
  | "health_potion_medium"
  | "health_potion_large";

export function tierFromStashItemId(itemId: string): HealthPotionTier | null {
  if (itemId === "health_potion_small") return "small";
  if (itemId === "health_potion_medium") return "medium";
  if (itemId === "health_potion_large") return "large";
  return null;
}

export function stashItemIdForTier(tier: HealthPotionTier): HealthPotionStashItemId {
  if (tier === "small") return "health_potion_small";
  if (tier === "medium") return "health_potion_medium";
  return "health_potion_large";
}

export function fulfillmentForTier(tier: HealthPotionTier): HealthPotionFulfillmentKind {
  if (tier === "small") return "health_potion_small";
  if (tier === "medium") return "health_potion_medium";
  return "health_potion_large";
}

export function tierFromFulfillment(fulfillment: string): HealthPotionTier | null {
  if (fulfillment === "health_potion" || fulfillment === "health_potion_small") return "small";
  if (fulfillment === "health_potion_medium") return "medium";
  if (fulfillment === "health_potion_large") return "large";
  return null;
}

export function walletKeyForTier(tier: HealthPotionTier): keyof HomeWallet {
  if (tier === "small") return "healthPotionsSmall";
  if (tier === "medium") return "healthPotionsMedium";
  return "healthPotionsLarge";
}

export function healthPotionCount(economy: HomeEconomy, tier: HealthPotionTier): number {
  return economy.wallet[walletKeyForTier(tier)];
}

export function totalHealthPotions(wallet: HomeWallet): number {
  return (
    wallet.healthPotionsSmall + wallet.healthPotionsMedium + wallet.healthPotionsLarge
  );
}

export function potionButtonId(prefix: "home" | "tower", tier: HealthPotionTier): string {
  return `${prefix}-use-potion-${tier}`;
}

export function potionQtyId(prefix: "home" | "tower", tier: HealthPotionTier): string {
  return `${prefix}-potion-slot-qty-${tier}`;
}

export function addHealthPotionsToWallet(
  wallet: HomeWallet,
  tier: HealthPotionTier,
  quantity: number,
): HomeWallet {
  const key = walletKeyForTier(tier);
  return {
    ...wallet,
    [key]: Math.max(0, wallet[key] + Math.floor(quantity)),
  };
}

export function healAmountForTier(tier: HealthPotionTier): number {
  return HEALTH_POTION_HEAL_BY_TIER[tier];
}
